import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Person, RosterPlayer as Player, TeamEdit } from '@y-sports/api-client';
import type { CreateTeamFromTemplateRequest, TeamBasicStatus } from '../features/teams/legacyModel';
import {
  TeamDataService,
  type CreateTeamPayload,
  type ManagedTeamDocument,
  type ManagedTeamParticipationHistoryEntryPayload,
  type TournamentRegistrationPayload,
} from '../services/TeamDataService';

export const teamQueryKeys = {
  all: ['teams'] as const,
  detail: (teamId: string) => [...teamQueryKeys.all, 'detail', teamId] as const,
  matches: (teamId: string) => [...teamQueryKeys.all, 'matches', teamId] as const,
  participationHistory: (teamId: string) => [...teamQueryKeys.all, 'participation-history', teamId] as const,
  documents: (teamId: string) => [...teamQueryKeys.all, 'documents', teamId] as const,
  managed: (email: string) => [...teamQueryKeys.all, 'managed', email] as const,
  registrations: (teamId: string, email: string) => [...teamQueryKeys.all, 'registrations', teamId, email] as const,
  activeTemplates: () => [...teamQueryKeys.all, 'active-templates'] as const,
  tournamentTeams: (tournamentId: string) => [...teamQueryKeys.all, 'tournament', tournamentId] as const,
};

export function useActiveTeamRegistrationTemplatesQuery() {
  return useQuery({
    queryKey: teamQueryKeys.activeTemplates(),
    queryFn: TeamDataService.getActiveTeamRegistrationTemplates,
  });
}

export function useTournamentTeamsSummaryQuery(tournamentId?: string) {
  return useQuery({
    queryKey: tournamentId ? teamQueryKeys.tournamentTeams(tournamentId) : teamQueryKeys.tournamentTeams('missing'),
    queryFn: () => TeamDataService.getTournamentTeamsSummary(tournamentId!),
    enabled: !!tournamentId,
  });
}

export function useUpdateTeamStatusMutation(tournamentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, status }: { teamId: string; status: TeamBasicStatus }) => TeamDataService.updateTournamentTeamStatus(tournamentId!, teamId, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.detail(variables.teamId) });
      if (tournamentId) {
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.tournamentTeams(tournamentId) });
      }
    },
  });
}

export function useTeamDetailQuery(teamId?: string) {
  return useQuery({
    queryKey: teamId ? teamQueryKeys.detail(teamId) : teamQueryKeys.detail('missing'),
    queryFn: () => TeamDataService.getTeamById(teamId!),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
  });
}

export function useTeamMatchesQuery(teamId?: string) {
  return useQuery({
    queryKey: teamId ? teamQueryKeys.matches(teamId) : teamQueryKeys.matches('missing'),
    queryFn: () => TeamDataService.getTeamMatches(teamId!),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
  });
}

export function useParticipationHistoryQuery(teamId?: string) {
  return useQuery({
    queryKey: teamId ? teamQueryKeys.participationHistory(teamId) : teamQueryKeys.participationHistory('missing'),
    queryFn: () => TeamDataService.getParticipationHistory(teamId!),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
  });
}

export function useTeamDocumentsQuery(teamId?: string, enabled = true) {
  return useQuery({
    queryKey: teamId ? teamQueryKeys.documents(teamId) : teamQueryKeys.documents('missing'),
    queryFn: () => TeamDataService.getTeamDocuments(teamId!),
    enabled: enabled && !!teamId,
    refetchOnWindowFocus: false,
  });
}

export function useTeamRegistrationsQuery(teamId?: string, email?: string) {
  return useQuery({
    queryKey: teamQueryKeys.registrations(teamId ?? 'missing', email ?? 'missing'),
    queryFn: () => TeamDataService.getUserTeamRegistrations(email!, teamId),
    enabled: !!teamId && !!email,
  });
}

export function useUpdateTeamBasicInfoMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamEdit: TeamEdit) => TeamDataService.updateTeamBasicInfo(teamId!, teamEdit),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.detail(teamId) });
    },
  });
}

export function useCreateParticipationHistoryMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ManagedTeamParticipationHistoryEntryPayload) => TeamDataService.createParticipationHistoryEntry(teamId!, payload),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.participationHistory(teamId) });
    },
  });
}

export function useUpdateParticipationHistoryMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: ManagedTeamParticipationHistoryEntryPayload }) =>
      TeamDataService.updateParticipationHistoryEntry(teamId!, entryId, payload),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.participationHistory(teamId) });
    },
  });
}

export function useDeleteParticipationHistoryMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => TeamDataService.deleteParticipationHistoryEntry(teamId!, entryId),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.participationHistory(teamId) });
    },
  });
}

export function useUploadTeamDocumentMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { fileName: string; description?: string; file: File }) =>
      TeamDataService.uploadTeamDocument(teamId!, payload),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.documents(teamId) });
    },
  });
}

export function useDownloadTeamDocumentMutation(teamId?: string) {
  return useMutation({
    mutationFn: (document: ManagedTeamDocument) => TeamDataService.downloadTeamDocument(teamId!, document.id),
  });
}

export function useViewTeamDocumentMutation(teamId?: string) {
  return useMutation({
    mutationFn: (document: ManagedTeamDocument) => TeamDataService.viewTeamDocument(teamId!, document.id),
  });
}

export function useReplaceTeamDocumentMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, file }: { documentId: string; file: File }) =>
      TeamDataService.replaceTeamDocumentContent(teamId!, documentId, file),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.documents(teamId) });
    },
  });
}

export function useDeleteTeamDocumentMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => TeamDataService.deleteTeamDocument(teamId!, documentId),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.documents(teamId) });
    },
  });
}

export function useManagedTeamsQuery(email?: string) {
  return useQuery({
    queryKey: teamQueryKeys.managed(email ?? 'missing'),
    queryFn: () => TeamDataService.getManagedTeams(email!),
    enabled: !!email,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateTeamPlayersMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (players: Player[]) => TeamDataService.updateTeamPlayers(teamId!, players),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.detail(teamId) });
    },
  });
}

export function useUpdateTeamOfficialsMutation(teamId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (officials: Person[]) => TeamDataService.updateTeamOfficials(teamId!, officials),
    onSuccess: () => {
      if (!teamId) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: teamQueryKeys.detail(teamId) });
    },
  });
}

export function useRegisterTeamInTournamentMutation(tournamentId?: string, email?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TournamentRegistrationPayload) => TeamDataService.registerTeamInTournament(tournamentId!, payload),
    onSuccess: () => {
      if (tournamentId) {
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.tournamentTeams(tournamentId) });
      }
      if (email) {
        queryClient.invalidateQueries({ queryKey: ['tournaments', 'user-stats', email] });
      }
    },
  });
}

export function useCreateTeamFromTemplateMutation() {
  return useMutation({
    mutationFn: (payload: CreateTeamFromTemplateRequest) => TeamDataService.createTeamFromTemplate(payload),
  });
}

export function useCreateTeamMutation() {
  return useMutation({
    mutationFn: (payload: CreateTeamPayload) => TeamDataService.createTeam(payload),
  });
}
